import { ProductGroup as Group } from '.';

enum Feature {
  KSE = 'KSE',
  ENGINE = 'ENGINE',
  FLOATING_ENGINE = 'FLOATING_ENGINE', // we dont use this
  UNLIMITED_KSE = 'UNLIMITED_KSE',
  UNLIMITED_ENGINE = 'UNLIMITED_ENGINE',
  TESTOPS = 'TESTOPS', // dont use this, just use for filtering TO license
  TESTOPS_BUSINESS = 'TESTOPS_BUSINESS',
  TESTOPS_ENTERPRISE = 'TESTOPS_ENTERPRISE',
  PER_USER_KSE = 'PER_USER_KSE',
  TESTCLOUD_MINUTE = 'TESTCLOUD_MINUTE',
  TESTCLOUD_SESSION_WEB = 'TESTCLOUD_SESSION_WEB',
  TESTCLOUD_FREE_TRIAL = 'TESTCLOUD_FREE_TRIAL',
  VISUAL_TESTING_PRO = 'VISUAL_TESTING_PRO',
  VISUAL_TESTING_STANDARD = 'VISUAL_TESTING_STANDARD',
  RECORDER = 'RECORDER',
  TESTOPS_PLATFORM = 'TESTOPS_PLATFORM',
  PER_USER_KSE_OFFLINE = 'PER_USER_KSE_OFFLINE',
  UNLIMITED_ENGINE_OFFLINE = 'UNLIMITED_ENGINE_OFFLINE',
  TESTCLOUD_SESSION_WEB_DESKTOP = 'TESTCLOUD_SESSION_WEB_DESKTOP',
  TESTCLOUD_SESSION_CROSS_BROWSER = 'TESTCLOUD_SESSION_CROSS_BROWSER',
  TESTCLOUD_SESSION_MOBILE_APP = 'TESTCLOUD_SESSION_MOBILE_APP',
  TESTCLOUD_SESSION_PRIVATE_DEVICE = 'TESTCLOUD_SESSION_PRIVATE_DEVICE',
  TESTCLOUD_SESSION_LIVE_TESTING = 'TESTCLOUD_SESSION_LIVE_TESTING',
  TRUE_TEST = 'TRUE_TEST',
  TESTOPS_G3 = 'TESTOPS_G3',
  PER_USER_KSE_EDUCATION = 'PER_USER_KSE_EDUCATION',
  FLOATING_KSE = 'FLOATING_KSE',
  TESTOPS_FLOATING = 'TESTOPS_FLOATING',
  TRUE_TEAM = 'TRUE_TEAM',
  TRUE_ENTERPRISE = 'TRUE_ENTERPRISE',
  TRUE_PLATFORM_ONLY = 'TRUE_PLATFORM_ONLY',
  TRUE_AUTOMATION = 'TRUE_AUTOMATION',
  // Internal account-level baselines auto-granted alongside any paid subscription
  // (KAM admin seats + TestOps guest seats). Not independently purchasable/renewable.
  TESTOPS_G3_ADMINISTRATOR = 'TESTOPS_G3_ADMINISTRATOR',
  TESTOPS_G3_GUEST_BASELINE = 'TESTOPS_G3_GUEST_BASELINE',
}

// https://github.com/typescript-eslint/typescript-eslint/issues/3568
// eslint-disable-next-line @typescript-eslint/no-redeclare
namespace Feature {
  export const fromString = (str: string): Feature | undefined => {
    if (str === 'KATALON_PLATFORM') {
      return Feature.TESTOPS_PLATFORM;
    }
    if (str === Feature.TESTOPS_PLATFORM.toString()) {
      return undefined;
    }
    return (Feature as any)[str];
  };
  export const toString = (feature: Feature): string => {
    if (feature === Feature.TESTOPS_PLATFORM) {
      return 'KATALON_PLATFORM';
    }
    return feature.toString();
  };

  export const getFeaturePrefix = (
    feature?: Feature | undefined,
  ) => {
    switch (feature) {
      case Feature.PER_USER_KSE:
      case Feature.PER_USER_KSE_OFFLINE:
        return 'kse';
      case Feature.UNLIMITED_ENGINE:
      case Feature.UNLIMITED_ENGINE_OFFLINE:
        return 'kre';
      case Feature.TESTCLOUD_SESSION_WEB:
      case Feature.TESTCLOUD_MINUTE:
        return 'testCloud';
      case Feature.TESTCLOUD_SESSION_WEB_DESKTOP:
        return 'testCloudDesktop';
      case Feature.TESTCLOUD_SESSION_CROSS_BROWSER:
        return 'testCloudCrossBrowser';
      case Feature.TESTCLOUD_SESSION_MOBILE_APP:
        return 'testCloudMobile';
      case Feature.TESTCLOUD_SESSION_PRIVATE_DEVICE:
        return 'testCloudPrivateDevice';
      case Feature.TESTCLOUD_SESSION_LIVE_TESTING:
        return 'testCloudLiveTesting';
      case Feature.VISUAL_TESTING_PRO:
      case Feature.VISUAL_TESTING_STANDARD:
        return 'visualTesting';
      case Feature.TESTOPS_PLATFORM:
        return 'platform';
      case Feature.TRUE_TEST:
        return 'truetest';
      case Feature.TESTOPS_G3:
        return 'testOpsG3';
      case Feature.TESTOPS_FLOATING:
        return 'testopsFloating';
      case Feature.TRUE_TEAM:
        return 'trueTeam';
      case Feature.TRUE_ENTERPRISE:
        return 'trueEnterprise';
      case Feature.TRUE_PLATFORM_ONLY:
        return 'truePlatformOnly';
      case Feature.TRUE_AUTOMATION:
        return 'trueAutomation';
      default:
        return 'Unknown';
    }
  };

  export const getFeatureGroup = (feature: Feature | undefined) => {
    switch (feature) {
      case Feature.PER_USER_KSE:
      case Feature.FLOATING_KSE:
        return Group.TEST_CREATION;
      case Feature.UNLIMITED_ENGINE:
      case Feature.TESTCLOUD_SESSION_WEB_DESKTOP:
      case Feature.TESTCLOUD_SESSION_CROSS_BROWSER:
      case Feature.TESTCLOUD_SESSION_MOBILE_APP:
      case Feature.TESTCLOUD_SESSION_LIVE_TESTING:
        return Group.TEST_EXECUTION;
      case Feature.TESTOPS_G3:
        return Group.TESTOPS;
      case Feature.TRUE_TEST:
        return Group.ADVANCED_WEB_TESTING;
      case Feature.TESTOPS_FLOATING:
        return Group.TESTOPS;
      default:
        return Group.TEST_CREATION;
    }
  };

  export const trueSelfServeFeature: Feature[] = [
    Feature.TRUE_TEAM,
    Feature.TRUE_PLATFORM_ONLY,
    Feature.TRUE_AUTOMATION,
    Feature.TESTCLOUD_SESSION_CROSS_BROWSER,
    Feature.TESTCLOUD_SESSION_MOBILE_APP,
    Feature.TESTCLOUD_SESSION_PRIVATE_DEVICE,
  ];

  export const trueDirectSaleFeature: Feature[] = [
    Feature.TRUE_ENTERPRISE,
  ];

  export const selfServeFeature: Feature[] = [
    Feature.PER_USER_KSE,
    Feature.UNLIMITED_ENGINE,
    Feature.TESTCLOUD_SESSION_WEB_DESKTOP,
    Feature.TESTCLOUD_SESSION_CROSS_BROWSER,
    Feature.TESTCLOUD_SESSION_MOBILE_APP,
    Feature.TESTCLOUD_SESSION_LIVE_TESTING,
  ];

  export const directSaleFeature: Feature[] = [
    Feature.TESTOPS_G3,
    Feature.TRUE_TEST,
  ];
}

// Internal baseline entitlements auto-granted with any paid subscription. They must NOT be shown as
// standalone subscription cards — they are bundled freebies, not purchasable/renewable products.
const BASELINE_FEATURES: Feature[] = [
  Feature.TESTOPS_G3_ADMINISTRATOR,
  Feature.TESTOPS_G3_GUEST_BASELINE,
];

export const isBaselineFeature = (feature: Feature) => BASELINE_FEATURES.includes(feature);

export const isTruePlatformFeature = (feature: Feature) => [
  Feature.TRUE_TEST,
  Feature.TRUE_TEAM,
  Feature.TRUE_ENTERPRISE,
  Feature.TRUE_PLATFORM_ONLY,
  Feature.TRUE_AUTOMATION,
].includes(feature);

const isStudioFeature = (feature: Feature) => [
  Feature.KSE,
  Feature.PER_USER_KSE,
  Feature.UNLIMITED_KSE,
  Feature.ENGINE,
  Feature.UNLIMITED_ENGINE,
  Feature.PER_USER_KSE_OFFLINE,
  Feature.UNLIMITED_ENGINE_OFFLINE,
  Feature.FLOATING_KSE,
].includes(feature);

export const isVisualTestingFeature = (feature: Feature) => [
  Feature.VISUAL_TESTING_STANDARD,
  Feature.VISUAL_TESTING_PRO,
].includes(feature);

export const isTestOpsPlatformFeature = (feature: Feature) => [
  Feature.TESTOPS_PLATFORM,
  Feature.TESTOPS_FLOATING,
].includes(feature);

export const isTestCloudFeature = (feature: Feature | undefined) => feature && [
  Feature.TESTCLOUD_MINUTE,
  Feature.TESTCLOUD_SESSION_WEB,
  Feature.TESTCLOUD_FREE_TRIAL,
  Feature.TESTCLOUD_SESSION_WEB_DESKTOP,
  Feature.TESTCLOUD_SESSION_CROSS_BROWSER,
  Feature.TESTCLOUD_SESSION_MOBILE_APP,
  Feature.TESTCLOUD_SESSION_PRIVATE_DEVICE,
  Feature.TESTCLOUD_SESSION_LIVE_TESTING,
].includes(feature);

export const getFeatureFromPlan = (planId: string): Feature | undefined => {
  if (planId.startsWith('testops_platform')) {
    return Feature.TESTOPS_PLATFORM;
  }

  if (planId.startsWith('testcloud_session_webdesktop')) {
    return Feature.TESTCLOUD_SESSION_WEB_DESKTOP;
  }

  if (planId.startsWith('testcloud_session_crossbrowser')) {
    return Feature.TESTCLOUD_SESSION_CROSS_BROWSER;
  }

  if (planId.startsWith('testcloud_session_web')) {
    return Feature.TESTCLOUD_SESSION_WEB;
  }

  if (planId.startsWith('testcloud_session_mobileapp')) {
    return Feature.TESTCLOUD_SESSION_MOBILE_APP;
  }

  if (planId.startsWith('testcloud_session_livetesting')) {
    return Feature.TESTCLOUD_SESSION_LIVE_TESTING;
  }

  if (planId.startsWith('testcloud_session_privatedevice')) {
    return Feature.TESTCLOUD_SESSION_PRIVATE_DEVICE;
  }

  if (planId.startsWith('visual_testing_pro')) {
    return Feature.VISUAL_TESTING_PRO;
  }

  if (planId.startsWith('kse_per-user')) {
    return Feature.PER_USER_KSE;
  }

  if (planId.startsWith('kse_floating-user')) {
    return Feature.FLOATING_KSE;
  }

  if (planId.startsWith('kre_floating')) {
    return Feature.UNLIMITED_ENGINE;
  }

  if (planId.startsWith('true_test')) {
    return Feature.TRUE_TEST;
  }

  if (planId.startsWith('testops_g3')) {
    return Feature.TESTOPS_G3;
  }

  if (planId.startsWith('testops_floating-user')) {
    return Feature.TESTOPS_FLOATING;
  }

  if (planId.startsWith('true_platform_teams')) {
    return Feature.TRUE_TEAM;
  }

  if (planId.startsWith('true_platform_enterprises')) {
    return Feature.TRUE_ENTERPRISE;
  }

  if (planId.startsWith('true_platform')) {
    return Feature.TRUE_PLATFORM_ONLY;
  }

  if (planId.startsWith('true_automation')) {
    return Feature.TRUE_AUTOMATION;
  }

  return undefined;
};

export const getProductId = (feature: Feature): string => {
  switch (feature) {
    case Feature.TESTOPS_PLATFORM:
      return 'testops_platform';
    case Feature.TESTCLOUD_SESSION_WEB_DESKTOP:
      return 'testcloud_session_webdesktop';
    case Feature.TESTCLOUD_SESSION_CROSS_BROWSER:
      return 'testcloud_session_crossbrowser';
    case Feature.TESTCLOUD_SESSION_WEB:
      return 'testcloud_session_web';
    case Feature.TESTCLOUD_SESSION_MOBILE_APP:
      return 'testcloud_session_mobileapp';
    case Feature.TESTCLOUD_SESSION_LIVE_TESTING:
      return 'testcloud_session_livetesting';
    case Feature.VISUAL_TESTING_PRO:
      return 'visual_testing_pro';
    case Feature.PER_USER_KSE:
      return 'kse_per-user';
    case Feature.FLOATING_KSE:
      return 'kse_floating-user';
    case Feature.UNLIMITED_ENGINE:
      return 'kre_floating';
    case Feature.TRUE_TEST:
      return 'true_test';
    case Feature.TESTOPS_G3:
      return 'testops_g3';
    case Feature.TESTOPS_FLOATING:
      return 'testops_floating-user';
    case Feature.TRUE_TEAM:
      return 'true_platform_teams';
    case Feature.TRUE_ENTERPRISE:
      return 'true_platform_enterprises';
    case Feature.TRUE_PLATFORM_ONLY:
      return 'true_platform';
    case Feature.TRUE_AUTOMATION:
      return 'true_automation';
    default:
      return '';
  }
};

export const getIntervalPathParam = (feature?: Feature): string => `${Feature.getFeaturePrefix(feature)}Cycle`;

export const getQuotaPathParam = (feature?: Feature): string => `${Feature.getFeaturePrefix(feature)}Quota`;

export const getRecurlySubUuIdPathParam = (
  feature?: Feature,
): string => `${Feature.getFeaturePrefix(feature)}RecurlySubUuId`;

export const isSameFeature = (
  planId1: string,
  planId2: string,
) => getFeatureFromPlan(planId1) === getFeatureFromPlan(planId2);

/**
 * Plan-code equality that treats a self-serve `_ecom_` plan code as the same plan as its
 * default sibling (e.g. `kse_per-user_ecom_annual` ~ `kse_per-user_annual`). The BE v2
 * preview/checkout routes eligible accounts' legacy KSE codes to the ecom variant
 * (product#1447) while the FE keeps sending legacy codes, so preview-response products
 * must be joined back to the request's code with this instead of `===`.
 */
export const isSamePlanIgnoringEcom = (
  planId1: string,
  planId2: string,
) => planId1.replace('_ecom_', '_') === planId2.replace('_ecom_', '_');

/**
 * Self-serve `_ecom_` sibling of a plan code (e.g. `kse_per-user_annual` →
 * `kse_per-user_ecom_annual`), mirroring the BE `toEcomPlanCode` convention of inserting
 * `ecom` before the billing-interval segment. Ecom codes are returned unchanged.
 */
export const toEcomPlanCode = (planId: string) => (
  planId.includes('_ecom_') ? planId : planId.replace(/_([^_]+)$/, '_ecom_$1')
);

export const getProductUnit = (feature: Feature | undefined) => {
  if (feature && (isStudioFeature(feature) || isTruePlatformFeature(feature))) {
    return 'License(s)';
  }
  if (feature && isVisualTestingFeature(feature)) {
    return 'Checkpoint Images';
  }
  if (feature && isTestCloudFeature(feature)) {
    return 'Session(s)';
  }
  if (feature && isTestOpsPlatformFeature(feature)) {
    return 'Test Results';
  }

  return '';
};

export { Feature };
